import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text } from '@reservamos/elements';
import { Field } from 'redux-form';
import { fieldRenderer, FilterableSelectRenderer, selectRenderer } from 'utils/formRenderers';
import { useTranslation } from 'react-i18next';
import {
  city,
  country,
  neighborhood,
  required,
  streetName,
  streetNumber,
  zipCode,
} from 'utils/formValidations';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useMasterCities from 'hooks/useMasterCities';

/**
 * AddressFields component.
 * Renders a form section with address-related input fields using redux-form.
 *
 * The city slot has three shapes, in precedence order:
 *
 * 1. **Caller-supplied catalog** (`cityOptions` + `cityFieldName`): a searchable select bound
 *    to that field — this is how the billing-company flow reuses the address city rather than
 *    rendering a second one. Always required: it identifies the company being created, so
 *    tenant address optionality does not apply to it.
 * 2. **Tenant master-cities catalog** (`useMasterCities`, e.g. Flota Ospina): a select bound to
 *    `addressAttributes.city` whose value is the numeric provider city id `crearTercero` needs.
 * 3. **Free text** bound to `addressAttributes.city`, whose requiredness follows the tenant's
 *    `optionalPurchaserAddressFields`.
 *
 * The two catalogs never collide: a tenant on the billing-company flow passes `cityOptions`,
 * and a tenant on master cities does not. Both are off by default.
 *
 * In `minimal` mode (the third-party invoice recipient) only Address + City are shown — the
 * data `crearTercero` needs — without the country gate.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.minimal - Render only Address + City.
 * @param {Array<{value: string, label: string}>} props.cityOptions - City catalog. When empty the city falls back to master cities or free text.
 * @param {string} props.cityFieldName - redux-form field the city select writes to. Required to use the catalog.
 * @param {Function} props.onCityChange - Called with `(event, newValue)` when a catalog city is picked.
 * @returns {JSX.Element|null} A group of address form fields including zip code, street name,
 * street number, neighborhood, city, and country.
 */
const AddressFields = ({ minimal, cityOptions, cityFieldName, onCityChange }) => {
  const { t } = useTranslation(['payment', 'passengers']);
  const { purchaserCountries } = useWhitelabelEnvs();
  // Named apart from the `cityOptions` prop on purpose: that one is a caller-supplied catalog
  // written to `cityFieldName`, this one is the tenant's master-cities catalog written to
  // `addressAttributes.city`. Same shape, different destination.
  const {
    options: masterCityOptions,
    enabled: cityCatalogEnabled,
    error: cityCatalogError,
    retry: retryCityCatalog,
  } = useMasterCities();

  // The master-cities catalog turns the city into a provider id, which only the
  // third-party invoice flow (crearTercero) consumes — that flow renders this
  // component with `minimal`. Scope the id-based select to it: the full purchaser
  // address (legacy NIT) and the MercadoPago PSE form (`TransferForm`) render
  // AddressFields without `minimal` and must keep free text, or an in-flight
  // purchase whose city is stored as free text (e.g. "Bogotá") shows an empty
  // select and leaves the purchaser dirty until re-picked.
  const useMasterCityCatalog = cityCatalogEnabled && minimal;

  // When the catalog applies the city must be a real provider id, so render the
  // required select even while the catalog is still loading or came back empty —
  // never the free-text field, which would let an arbitrary city through
  // validation.
  const cityField = useMasterCityCatalog ? (
    <Spacing size="XS" isResponsive vertical>
      <Field
        id="city"
        name="addressAttributes.city"
        label={t('label.city')}
        placeholder={t('label.city')}
        options={masterCityOptions}
        component={selectRenderer}
        validate={required}
      />
      {cityCatalogError && (
        <button type="button" className="text-left underline" onClick={retryCityCatalog}>
          <Text size="XS" color="error">
            {t('passengers:city_catalog_error')}
          </Text>
        </button>
      )}
    </Spacing>
  ) : (
    <Field
      id="city"
      type="text"
      name="addressAttributes.city"
      label={t('label.city')}
      placeholder={t('label.city')}
      component={fieldRenderer}
      validate={city}
    />
  );

  const streetField = (
    <Field
      id="streetName"
      type="text"
      name="addressAttributes.street"
      label={t('label.street_name')}
      placeholder={t('label.street_name')}
      component={fieldRenderer}
      validate={streetName}
    />
  );

  if (minimal) {
    return (
      <Spacing size="S" isResponsive vertical>
        <Text size="M" weight="bold">
          {t('label.address')}
        </Text>
        <Spacing size="S" isResponsive flexGrow>
          {streetField}
          {cityField}
        </Spacing>
      </Spacing>
    );
  }

  if (!purchaserCountries) return null;

  const usesCityCatalog = Boolean(cityFieldName) && cityOptions.length > 0;

  return (
    <Spacing size="S" isResponsive vertical>
      <Text size="M" weight="bold">
        {t('label.address')}
      </Text>
      <Spacing size="S" isResponsive flexGrow>
        <Field
          id="zipCode"
          type="text"
          name="addressAttributes.zipCode"
          label={t('label.zip_code')}
          placeholder={t('label.zip_code')}
          component={fieldRenderer}
          validate={zipCode}
        />
        {streetField}
        <Field
          id="streetNumber"
          type="text"
          name="addressAttributes.number"
          label={t('label.street_number')}
          placeholder={t('label.street_number')}
          component={fieldRenderer}
          validate={streetNumber}
        />
      </Spacing>
      <Spacing size="S" isResponsive flexGrow>
        <Field
          id="neighborhood"
          type="text"
          name="addressAttributes.neighborhood"
          label={t('label.neighborhood')}
          placeholder={t('label.neighborhood')}
          component={fieldRenderer}
          validate={neighborhood}
        />
        {usesCityCatalog ? (
          <Field
            id="city"
            name={cityFieldName}
            label={t('label.city')}
            placeholder={t('label.city')}
            options={cityOptions}
            noOptionsText={t('general:no_options_found')}
            component={FilterableSelectRenderer}
            onChange={onCityChange}
            validate={required}
          />
        ) : (
          // Falls back to master cities, then to free text — see `cityField`.
          cityField
        )}
        <Field
          id="country"
          name="addressAttributes.countryCode"
          label={t('label.country')}
          placeholder={t('label.country')}
          options={purchaserCountries}
          noOptionsText={t('general:no_options_found')}
          component={FilterableSelectRenderer}
          validate={country}
        />
      </Spacing>
    </Spacing>
  );
};

AddressFields.propTypes = {
  minimal: PropTypes.bool,
  cityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  cityFieldName: PropTypes.string,
  onCityChange: PropTypes.func,
};

AddressFields.defaultProps = {
  minimal: false,
  cityOptions: [],
  cityFieldName: '',
  onCityChange: undefined,
};

export default AddressFields;
